import { useEffect, useRef, useCallback, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import exportDataService from "@/services/exportDataService";
import { downloadFileFromUrl } from "@/lib/file";
import { ExportJobResponse, ExportStatusResponse } from "@/types/exportData";
import { useToast } from "@/components/ui/use-toast";
import { t } from "i18next";
import appConfiguration from "@/components/cfg/AppCfg";

interface UseExportDataArgs {
  moduleName: string;
  format: "csv" | "xlsx";
  params?: URLSearchParams;
  enabled?: boolean;
  onSuccess?: () => void;
}

/**
 * Checks if the export has exceeded the timeout limit.
 */
function isExportTimeout(startTime: number | null, timeoutMs: number): boolean {
  if (!startTime) return false;
  return Date.now() - startTime > timeoutMs;
}

export function useExportData({
  moduleName,
  format,
  params,
  enabled = true,
  onSuccess,
}: UseExportDataArgs) {
  const { toast } = useToast();
  const startTimeRef = useRef<number | null>(null);
  const downloadedJobIdRef = useRef<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const startMutation = useMutation<ExportJobResponse>({
    mutationFn: () =>
      exportDataService.startExportData(moduleName, format, params),
    onSuccess: () => {
      startTimeRef.current = Date.now();
      downloadedJobIdRef.current = null;
    },
  });

  const jobId = startMutation.data?.job_id;

  const handleDownload = useCallback(
    async (url: string, filename: string) => {
      setIsDownloading(true);
      try {
        await downloadFileFromUrl(url, filename);
        onSuccess?.();
      } catch (error) {
        console.error("Failed to download file:", error);
        toast({
          title: t("exportDialog.errorTitle"),
          description: t("exportDialog.errorDescription"),
          variant: "destructive",
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [onSuccess, toast],
  );

  const statusQuery = useQuery<ExportStatusResponse>({
    queryKey: ["exportData", moduleName, jobId],
    queryFn: () =>
      exportDataService.getExportStatus(moduleName, jobId as string),
    enabled: enabled && Boolean(jobId) && Boolean(startTimeRef.current),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status !== "pending") return false;
      const isTimeout = isExportTimeout(
        startTimeRef.current,
        appConfiguration.exportTimeoutMs,
      );
      if (isTimeout) return false;
      return 2000;
    },
  });

  useEffect(() => {
    const data = statusQuery.data;
    if (!data || !jobId || !startTimeRef.current) return;

    if (data.status === "completed" && data.download_url && data.filename) {
      if (downloadedJobIdRef.current === jobId) return;

      downloadedJobIdRef.current = jobId;
      startTimeRef.current = null;
      handleDownload(data.download_url, data.filename);
      return;
    }

    if (data.status === "failed") {
      startTimeRef.current = null;
      downloadedJobIdRef.current = null;
      toast({
        title: t("exportDialog.errorTitle"),
        description: data.error_message || t("exportDialog.errorDescription"),
        variant: "destructive",
      });
      return;
    }

    if (
      data.status === "pending" &&
      isExportTimeout(startTimeRef.current, appConfiguration.exportTimeoutMs)
    ) {
      startTimeRef.current = null;
      downloadedJobIdRef.current = null;
      toast({
        title: t("exportDialog.errorTitle"),
        description: t("exportDialog.timeoutDescription"),
        variant: "destructive",
      });
    }
  }, [statusQuery.data, jobId, toast, handleDownload]);

  const reset = useCallback(() => {
    startMutation.reset();
    startTimeRef.current = null;
    downloadedJobIdRef.current = null;
    setIsDownloading(false);
  }, [startMutation.reset]);

  return {
    mutate: startMutation.mutate,
    reset,
    isPending:
      startMutation.isPending ||
      statusQuery.isFetching ||
      statusQuery.data?.status === "pending" ||
      isDownloading,
    isError: startMutation.isError || statusQuery.data?.status === "failed",
    errorMessage:
      statusQuery.data?.status === "failed"
        ? statusQuery.data.error_message
        : undefined,
  };
}
